import { Component, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { Subject, takeUntil, tap } from 'rxjs';
import { nameOfClass } from '../../../../../../core/functions/string-helpers';
import { DropdownOption } from '../../../../../../core/models/dropdown-option';
import { FormGroupType } from '../../../../../../core/models/form-group-type';
import { OrderStatus } from '../../../../../../modules/application/orders/shared/constants/order-status';
import { OrderFormService } from '../../../../../../modules/application/orders/shared/services/order-form/order-form.service';
import { OrderService } from '../../../../../../modules/application/orders/shared/services/order/order.service';
import { OrderStatusCellRendererParams } from './models/order-status-cell-renderer-params';
import { OrderStatusFormModel } from './models/order-status-form-model';
import { OrderStatusUpdateRequest } from './models/order-status-update-request';
import { OrderStatusCellRendererFormService } from './services/order-status-cell-renderer-form.service';

@Component({
    selector: 'app-order-status-cell-renderer',
    templateUrl: './order-status-cell-renderer.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [MatSelectModule, MatFormFieldModule, ReactiveFormsModule],
})
export class OrderStatusCellRendererComponent
    implements ICellRendererAngularComp, OnDestroy
{
    private destroy$ = new Subject<void>();

    params!: OrderStatusCellRendererParams;

    orderStatusDropdownOptions: DropdownOption[] = [];

    form: FormGroup<FormGroupType<OrderStatusFormModel>> =
        this.formService.getFormDefinition();

    constructor(
        private readonly orderFormService: OrderFormService,
        private readonly formService: OrderStatusCellRendererFormService,
        private readonly orderService: OrderService,
    ) {}

    agInit(params: OrderStatusCellRendererParams): void {
        this.params = params;

        this.bindForm(params);
        this.setupFormListeners();
        this.setupDropdownOptions();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    refresh(): boolean {
        return false;
    }

    private bindForm(params: OrderStatusCellRendererParams): void {
        this.form.patchValue({
            status: params.data.status,
        });
    }

    private generateRequestObject(): OrderStatusUpdateRequest {
        const formValue = this.form.value as Required<OrderStatusUpdateRequest>;

        return {
            ...formValue,
        };
    }

    private saveFormDataToApi(): void {
        const requestObject = this.generateRequestObject();

        this.orderService
            .updateOrderStatus(
                this.params.data.orderId.toString(),
                requestObject,
            )
            .subscribe();
    }

    private setupFormListeners(): void {
        this.form.valueChanges
            .pipe(
                takeUntil(this.destroy$),
                tap(() => {
                    this.updateOrderStatusAndRefreshGrid(
                        this.form.controls.status.value!,
                    );

                    this.saveFormDataToApi();
                }),
            )
            .subscribe();
    }

    /**
     * Updates the order status for the current grid row and refreshes the grid to reflect the change.
     *
     * @param orderStatus - The new order status enum value to set
     */
    private updateOrderStatusAndRefreshGrid(orderStatus: OrderStatus): void {
        this.params.data.status = orderStatus;

        // Refresh the specific cell to reflect the updated status
        this.params.api.refreshCells({
            rowNodes: [this.params.node],
            force: true,
        });

        // Notify the grid that a filter change may have occurred
        this.params.api.onFilterChanged();
    }

    private setupDropdownOptions(): void {
        const permittedOrderStatusTransitions =
            this.params.permittedOrderStatusTransitions;
        if (!permittedOrderStatusTransitions) {
            throw new Error(
                `Permitted order status transitions not provided to ${nameOfClass(OrderStatusCellRendererComponent)}.`,
            );
        }

        this.orderStatusDropdownOptions =
            this.orderFormService.getStatusTransitionDropdownOptions(
                permittedOrderStatusTransitions,
                this.params.data.status,
            );
    }
}
